import React from "react";
import styled from "styled-components";
import CharacterStudio from "../../CharacterStudio";
import { SocialConnectPanel } from "../../connections/SocialScheduling";
import { TikTokConnectPanel } from "../../connections/TikTokConnectPanel";
import { LinkedInConnectPanel } from "../../connections/LinkedInConnectPanel";
import { XConnectPanel } from "../../connections/XConnectPanel";
import { MetaConnectPanel } from "../../connections/MetaConnectPanel";
import { ThreadsConnectPanel } from "../../connections/ThreadsConnectPanel";
import { YouTubeConnectPanel } from "../../connections/YouTubeConnectPanel";
import {
  GEMINI_API_KEY,
  DEFAULT_ELEVENLABS_API_KEY,
} from "../../../plan/dayplan/components/dailyStep/mediaGeneratorConfig";
import { useMediaStudio } from "../../context/MediaStudioContext";
import VoiceOfCustomerEditor from "./VoiceOfCustomerEditor";
import FormatProfileEditor from "./FormatProfileEditor";
import VisualStyleEditor from "./VisualStyleEditor";
import PromptTemplatesEditor from "./PromptTemplatesEditor";
import {
  SectionRoot,
  SectionTitle,
  SectionSub,
  Card,
  CardTitle,
  CardHint,
} from "../shared";

// Everything that configures the studio, in one place: the brand research
// editors (formerly crammed into a 380px inspector), reel characters, social
// connections, and the reel prompt templates.

export type SettingsTab = "brand" | "characters" | "connections" | "prompts";

const Layout = styled.div`
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  gap: var(--space-4);
  align-items: start;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const SubNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: sticky;
  top: var(--space-4);
`;

const SubNavButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  text-align: left;
  padding: 8px 12px;
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: ${(p) =>
    p.$active ? "var(--font-weight-semibold)" : "var(--font-weight-medium)"};
  color: ${(p) => (p.$active ? "var(--color-text)" : "var(--color-text-secondary)")};
  background: ${(p) => (p.$active ? "var(--color-surface)" : "transparent")};
  box-shadow: ${(p) => (p.$active ? "var(--shadow-xs)" : "none")};
  border: 1px solid
    ${(p) => (p.$active ? "var(--color-border-subtle)" : "transparent")};
  cursor: pointer;

  &:hover {
    color: var(--color-text);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
`;

const TABS: { id: SettingsTab; label: string }[] = [
  { id: "brand", label: "Brand voice" },
  { id: "characters", label: "Characters" },
  { id: "connections", label: "Connections" },
  { id: "prompts", label: "Prompts" },
];

export type SettingsSectionProps = {
  tab: SettingsTab;
  onSelectTab: (tab: SettingsTab) => void;
};

export const SettingsSection: React.FC<SettingsSectionProps> = ({
  tab,
  onSelectTab,
}) => {
  const { communityId, mediaConfig } = useMediaStudio();

  return (
    <SectionRoot>
      <div>
        <SectionTitle>Settings</SectionTitle>
        <SectionSub>
          Brand research, reel characters, connected accounts, and generation
          prompts.
        </SectionSub>
      </div>

      <Layout>
        <SubNav>
          {TABS.map((t) => (
            <SubNavButton
              key={t.id}
              type="button"
              $active={tab === t.id}
              onClick={() => onSelectTab(t.id)}
            >
              {t.label}
            </SubNavButton>
          ))}
        </SubNav>

        <Content>
          {tab === "brand" && (
            <>
              <Card>
                <CardTitle>Audience voice</CardTitle>
                <CardHint>
                  Verbatim phrases your audience uses. Feeds every generator.
                </CardHint>
                <VoiceOfCustomerEditor
                  communityId={communityId}
                  initialValue={mediaConfig.voc}
                  onPersist={(next) => mediaConfig.setVoc(next)}
                />
              </Card>
              <Card>
                <CardTitle>Format profile</CardTitle>
                <CardHint>
                  Top-performing content formats for this audience. Biases what
                  the generators pick.
                </CardHint>
                <FormatProfileEditor
                  communityId={communityId}
                  initialValue={mediaConfig.formatProfile}
                  existingCustomFormats={mediaConfig.customFormats}
                  hasAudienceData={mediaConfig.hasAudienceData}
                  onPersist={(profile, addedCustomFormats) => {
                    mediaConfig.setFormatProfile({
                      formats: profile.formats,
                      lastResearchAt:
                        profile.lastResearchAt || new Date().toISOString(),
                    });
                    if (addedCustomFormats && addedCustomFormats.length > 0) {
                      mediaConfig.addCustomFormats(addedCustomFormats);
                    }
                  }}
                />
              </Card>
              <Card>
                <CardTitle>Visual style</CardTitle>
                <CardHint>
                  The aesthetic winning with this audience. Drives image
                  generation.
                </CardHint>
                <VisualStyleEditor
                  communityId={communityId}
                  initialValue={mediaConfig.visualStyleProfile}
                  hasAudienceData={mediaConfig.hasAudienceData}
                  onPersist={(next) => mediaConfig.setVisualStyleProfile(next)}
                />
              </Card>
            </>
          )}

          {tab === "characters" && (
            <Card>
              <CardTitle>Reel characters</CardTitle>
              <CardHint>
                Recurring AI characters for UGC style reels.
              </CardHint>
              <CharacterStudio
                communityId={communityId}
                geminiApiKey={GEMINI_API_KEY}
                elevenLabsApiKey={DEFAULT_ELEVENLABS_API_KEY}
              />
            </Card>
          )}

          {tab === "connections" && (
            <Card>
              <CardTitle>Connected accounts</CardTitle>
              <CardHint>
                Link the social accounts this community publishes to. Connected
                platforms publish first party. The rest go through the
                cross-post service.
              </CardHint>
              {communityId && (
                <div>
                  <SocialConnectPanel communityId={communityId} />
                  <TikTokConnectPanel communityId={communityId} />
                  <LinkedInConnectPanel communityId={communityId} />
                  <MetaConnectPanel communityId={communityId} />
                  <XConnectPanel communityId={communityId} />
                  <YouTubeConnectPanel communityId={communityId} />
                  <ThreadsConnectPanel communityId={communityId} />
                </div>
              )}
            </Card>
          )}

          {tab === "prompts" && (
            <Card>
              <CardTitle>Generation prompts</CardTitle>
              <CardHint>
                Advanced. Override the prompts the viral reel creator uses for
                this community.
              </CardHint>
              <PromptTemplatesEditor communityId={communityId} />
            </Card>
          )}
        </Content>
      </Layout>
    </SectionRoot>
  );
};

export default SettingsSection;
